import { Text2, skinVars, Row, Tag, RowList, IconInformationUserRegular, useDialog, Text5 } from '@telefonica/mistica';
import React, { useEffect, useRef } from 'react';
import './index.scss';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { showSnackbar } from '../../../utils/store/features/user';
import { formatMobileNumber } from '../../../utils/dataTransformation/unitsConversion';
import { ETypeUser } from '../../../utils/store/model/store.model';
import { TrackingEvents } from '../../../utils/dataTransformation/tracking';
import { getCustomerData } from '../../../services/CustomerService';
import useUserlight from '../../../hooks/useUserLight';
import Stopper from '../../../components/Stopper';
import { managementEnvironment } from '../../../utils/dataTransformation/environments';
import { isWebviewAndroid, isWebviewIOS } from '../../../utils/navigation/controlDevices';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useChangeOwnershipContext } from '../../../context/change-ownership-context/ChangeOwnershipContext';
import useChangeOwnership from '../../../hooks/useChangeOwnership';
import { usePersonalDataContext } from '../../../context/personal-data-context/PersonalDataContext';
import HeaderComponent from '../../../components/HeaderComponent';
import useGlobalEscalation from '../../../components/GlobalEscalation/GlobalEscalation';
import { isIOS } from 'react-device-detect';

export const PersonalData: React.FC = () => {
    const isLight = useUserlight();
    const navigate = useNavigate();
    const { dialog } = useDialog();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const typeUser = new URLSearchParams(window.location.search).get('clientType');
    const { trackingScreenView } = new TrackingEvents();
    const { checkEscalation } = useGlobalEscalation();
    const { setFlowStartUrl } = useChangeOwnershipContext();
    const { initializeChangeOwnership } = useChangeOwnership();
    const { data: personalData } = useQuery({ queryKey: ['customer-data'], queryFn: getCustomerData });
    const { setPersonalData } = usePersonalDataContext();
    const personalDataDisabled = managementEnvironment().VITE_STOPPER_PROFILE_CUSTOMER_DATA_DISABLED;
    const refFirstLoad = useRef<boolean>(true);
    const reftiemout = useRef<number>(0);
    const ownershipChangeEnabled = managementEnvironment().VITE_WEBVIEW_OWNERSHIP_CHANGE_ENABLED;

    useEffect(() => {
        if (!(isWebviewAndroid || isWebviewIOS)) {
            trackingScreenView();
        }

        refFirstLoad.current = false;
        return () => {
            refFirstLoad.current = true;

            window.clearTimeout(reftiemout.current);
        };
    }, []);

    useEffect(() => {
        setPersonalData(personalData?.data!);
    }, [personalData?.data]);

    const navigationBackHandler = () => navigate('/profile');

    return personalDataDisabled ? (
        <Stopper></Stopper>
    ) : (
        <div className='personal-data'>
            <div className='personal-data__header'>
                <HeaderComponent
                    title={t('personalDataTitle2')}
                    navigationBarOnBack={navigationBackHandler}
                    breadcrumbs={[{ title: t('profileTitle'), url: '/profile' }]}
                />
            </div>

            <div className='personal-data__content'>
                <div className='personal-data__content-titleContact'>
                    <Text5 color={skinVars.colors.textPrimary}>{t('personalDataTitle')}</Text5>
                </div>

                <div className='personal-data__content-boxedRow'>
                    <RowList>
                        <Row
                            headline={
                                <Tag type={personalData?.data.contactNumber?.verified ? 'success' : 'inactive'}>
                                    {personalData?.data.contactNumber?.verified
                                        ? t('contactVerificationStep1Verified')
                                        : t('contactVerificationStep1NotVerified')}
                                </Tag>
                            }
                            title={t('personalDataMobile')}
                            description={
                                personalData?.data.contactNumber?.value &&
                                formatMobileNumber(personalData?.data.contactNumber?.value)
                            }
                            extra={
                                !personalData?.data.contactNumber?.value && (
                                    <Text2 medium color={skinVars.colors.textLink}>
                                        {t('personalDataAddMobile')}
                                    </Text2>
                                )
                            }
                            onPress={() => {
                                if (typeUser === ETypeUser.PRECLIENT) return;
                                if (isLight) {
                                    dispatch(showSnackbar());
                                } else {
                                    if (isIOS) {
                                        const newWindow = window.open('', 'newWindow');
                                        sessionStorage.setItem('newWindow', newWindow?.name || '');
                                    }
                                    checkEscalation('/update-personal-data', 'mobile', {
                                        mobileVerified: personalData?.data.contactNumber?.verified,
                                        emailVerified: personalData?.data.email?.verified,
                                    });
                                }
                            }}
                            withChevron={typeUser !== ETypeUser.PRECLIENT}
                        />
                        <Row
                            headline={
                                <Tag type={personalData?.data.email?.verified ? 'success' : 'inactive'}>
                                    {personalData?.data.email?.verified
                                        ? t('contactVerificationStep1Verified')
                                        : t('contactVerificationStep1NotVerified')}
                                </Tag>
                            }
                            title={t('personalDataEmail')}
                            description={personalData?.data.email?.value}
                            extra={
                                !personalData?.data.email?.value && (
                                    <Text2 medium color={skinVars.colors.textLink}>
                                        {t('personalDataAddEmail')}
                                    </Text2>
                                )
                            }
                            onPress={() => {
                                if (typeUser === ETypeUser.PRECLIENT) return;
                                if (isLight) {
                                    dispatch(showSnackbar());
                                } else {
                                    if (isIOS) {
                                        const newWindow = window.open('', 'newWindow');
                                        sessionStorage.setItem('newWindow', newWindow?.name || '');
                                    }
                                    checkEscalation('/update-personal-data', 'email', {
                                        mobileVerified: personalData?.data.contactNumber?.verified,
                                        emailVerified: personalData?.data.email?.verified,
                                    });
                                }
                            }}
                            withChevron={typeUser !== ETypeUser.PRECLIENT}
                        />
                    </RowList>
                </div>
                {typeUser !== ETypeUser.PRECLIENT && (
                    <>
                        <div className='personal-data__content-titleBankAccount'>
                            <Text5 color={skinVars.colors.textPrimary}>{t('personalDataAccount')}</Text5>
                        </div>
                        <div className='personal-data__content-boxedRow'>
                            <Row
                                title={t('supportContentRowChangeBankAccount')}
                                onPress={() => {
                                    if (isLight) {
                                        dispatch(showSnackbar());
                                    } else {
                                        if (isIOS) {
                                            const newWindow = window.open('', 'newWindow');
                                            sessionStorage.setItem('newWindow', newWindow?.name || '');
                                        }
                                        checkEscalation('/select-bank-account', 'bankAccount', {
                                            mobileVerified: personalData?.data.contactNumber?.verified,
                                            emailVerified: personalData?.data.email?.verified,
                                        });
                                    }
                                }}
                                withChevron={typeUser !== ETypeUser.PRECLIENT}
                            />
                        </div>
                    </>
                )}
                {ownershipChangeEnabled && (
                    <>
                        <div className='personal-data__content-titleOwnership'>
                            <Text5 color={skinVars.colors.textPrimary}>{t('ownershipData')}</Text5>
                        </div>
                        <div className='personal-data__content-boxedRow'>
                            <Row
                                title={t('ChangeOfOwnership')}
                                description={
                                    personalData?.data.fiscalId.slice(0, -3).replace(/./g, '*')! +
                                    personalData?.data.fiscalId.slice(-3)!
                                }
                                onPress={() => {
                                    if (isLight) {
                                        dispatch(showSnackbar());
                                    } else if (personalData?.data.fiscalIdType.includes('CIF')) {
                                        dialog({
                                            asset: (
                                                <IconInformationUserRegular
                                                    color={skinVars.colors.backgroundBrandSecondary}
                                                    size={40}
                                                />
                                            ),
                                            title: t('changeHolderCifRestrictedTitle'),
                                            acceptText: t('buttonUnderstoodFeedback'),
                                            message: t('cifChangeÖwnershipDialog'),
                                            onAccept() {},
                                        });
                                    } else {
                                        if (isIOS) {
                                            const newWindow = window.open('', 'newWindow');
                                            sessionStorage.setItem('newWindow', newWindow?.name || '');
                                        }
                                        setFlowStartUrl('/profile/personal-data');
                                        initializeChangeOwnership();
                                    }
                                }}
                                withChevron={typeUser !== ETypeUser.PRECLIENT}
                            />
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default PersonalData;
